import { UUID } from '../../model/uuid';
import { v4 as uuidv4 } from 'uuid';
import { Token } from '../../model/structure';

type NodeKind = "span" | "token";
export type SpanTreeNode = TokenNode | SpanNode;

type MentionModel = "entity" | "situation";

export interface SpanTreeNodeModel {
    kind: NodeKind;
}

export type TaggedTokenSpan = {
    tokenizationId: UUID;
    spanId: UUID;
    tokenIndexList: number[];
    type?: string;
    situationKind?: string;
    ID?: string;
    mentionModel: MentionModel;
}

export interface TokenNodeModel extends SpanTreeNodeModel {
    kind: "token";
    subspanId: UUID;
    token: Token;
    tokenIndex: number;
    tokenIndexList: number[];
    text?: string;
}

export interface SpanNodeModel extends SpanTreeNodeModel {
    kind: "span";
    tokenizationId: UUID;
    spanId: UUID;
    subspanId: UUID;
    span: TaggedTokenSpan;
    tokenIndexList: number[];
    mentionModel?: "entity" | "situation";
    type: string;
}

/** A span node in a span tree. */
export class TokenNode implements TokenNodeModel {
    kind: "token" = "token";
    token: Token;
    subspanId: UUID;

    /** 
     * Create a token node.
     * 
     * @param token
     * The token this node wraps. 
     */
    constructor(token: Token) {
        this.token = token;
        this.subspanId = { 'uuidString': uuidv4() };
    }

    get tokenIndex() {
        return this.token.tokenIndex;
    }

    get tokenIndexList() {
        return [this.token.tokenIndex];
    }

    get text() {
        return this.token.text;
    }

    /** Split the span wrapped by this node into multiple spans.
     * 
     * This method just makes a copy of this node and wraps it in a list.
     * It is mainly intended to homogenize the SpanTreeNode interface.
     * 
     * @param index
     * If defined, the index at which to split the span (right exclusive).
     * If not defined, the span will be split only where discontiguous.
     */
    splitSpan(index?: number): SpanTreeNode[] {
        return [this];
    }
}


/** A span node in a span tree. */
export class SpanNode implements SpanNodeModel {
    kind: "span" = "span";
    span: TaggedTokenSpan;
    subspanId: UUID;
    mentionModel: "entity" | "situation";
    parentMentionModel?: MentionModel


    /** 
     * Create a span node.
     * 
     * @param span
     * The span this node wraps. 
     */
    constructor(span: TaggedTokenSpan) {
        this.span = span;
        this.subspanId = { 'uuidString': uuidv4() };
        this.mentionModel = span.mentionModel;
    }

    get tokenizationId() {
        return this.span.tokenizationId;
    }

    get spanId() {
        return this.span.spanId;
    }

    set spanId(spanId: UUID) {
        this.span.spanId = spanId;
    }

    get tokenIndexList() {
        const tokenIndexList = this.span.tokenIndexList.slice();

        tokenIndexList.sort((i, j) => i - j);

        return tokenIndexList;
    }

    set tokenIndexList(tokenIndexList: number[]) {
        this.span.tokenIndexList = tokenIndexList;
    }

    get type() {
        if (this.span.type !== undefined) {
            return this.span.type;
        }
        else {
            return "";
        }
    }

    set type(type: string) {
        this.span.type = type;
    }

    /** Split the span wrapped by this node into multiple spans.
     * 
     * @param index
     * If defined, the index at which to split the span (right exclusive).
     * If not defined, the span will be split only where discontiguous.
     */
    splitSpan(index?: number) {
        let newSpans: TaggedTokenSpan[] = [];

        if (index === undefined) {

            let isContiguous = true;
            let prevIndex = -1;

            for (let i = 0; i < this.tokenIndexList.length; i++) {
                if (i > 0 && this.tokenIndexList[i] > (prevIndex + 1)) {
                    isContiguous = false;
                    newSpans = this.splitSpan(this.tokenIndexList[i]).map(
                        n => n.splitSpan()
                    ).flat();
                }
                prevIndex = this.tokenIndexList[i];
            }

            if (isContiguous) {
                return [this];
            }

        } else {
            const start = Math.min(...this.tokenIndexList);
            const end = Math.max(...this.tokenIndexList);

            if (index <= start || index > end) {
                return [this];
            }

            newSpans.push(
                {
                    tokenizationId: this.tokenizationId,
                    spanId: this.spanId,
                    tokenIndexList: this.tokenIndexList.filter(
                        i => i < index
                    ),
                    type: this.type,
                    mentionModel: this.mentionModel          
                }
            );

            newSpans.push(
                {
                    tokenizationId: this.tokenizationId,
                    spanId: this.spanId,
                    tokenIndexList: this.tokenIndexList.filter(
                        i => i >= index
                    ),
                    type: this.type,
                    mentionModel: this.mentionModel
                }
            );
        }

        return newSpans.map(
            span => new SpanNode(span)
        );
    }

}

/** A tree representing containment relations between spans in a text. 
 * 
 * The spans wrapped by nodes in this tree are guaranteed not to overlap
 * with any other spans wrapped by nodes in this tree unless their is a 
 * containment relation between the spans.
 * 
 * The nodes in this tree are also guaranteed to be sorted in ascending order
 * based on the left edge (start) of the span they wrap. When two nodes/spans
 * start at the same position, sort order is adjudicated by size in descending
 * order.
*/
export class SpanTree {

    nodes: SpanTreeNode[];

    /**
     * Create a span tree.
     * 
     * @param tokenList
     * All tokens, whether in a span or not.
     * @param spanList 
     * The list of spans in the graph.
     */
    constructor(tokenList: Token[], spanList: TaggedTokenSpan[]) {
        // wrap the spans in nodes, splitting discontigous spans
        const spanNodes: SpanTreeNode[] = spanList.map(
            span => new SpanNode(span)
        ).map(
            node => node.splitSpan()
        ).flat();


        // wrap the tokens in nodes
        const tokenNodes: SpanTreeNode[] = tokenList.map(
            token => new TokenNode(token)
        );

        // concatenate the span and token nodes
        this.nodes = tokenNodes.concat(spanNodes);

        // convert the span graph to a span tree
        this._convertToTree()
    }

    _findSpanNodes(spanId: UUID | null) {
        return this.nodes.filter(
            (n: SpanTreeNode): n is SpanNode => (
                n.kind === "span" &&
                spanId?.uuidString === n.spanId.uuidString
            )
        );
    }

    _deleteSpan(SpanId: UUID) {
        this.nodes = this.nodes.filter(
            n => (n.kind === "span" && n.span.spanId.uuidString !== SpanId.uuidString) || n.kind === "token"
        );
        
        return this;
    }

    _deleteSubspan(subspanId: UUID) {
        this.nodes = this.nodes.filter(
            n => n.subspanId.uuidString !== subspanId.uuidString
        );
        return this;
    }

    _mergeSubspansOf(spanId: UUID) {
        const spanNodes = this._findSpanNodes(spanId);

        if (spanNodes.length > 1) {
            const tokenIndexList = spanNodes.map(
                n => n.tokenIndexList
            ).flat();
            spanNodes[0].tokenIndexList = tokenIndexList;
            spanNodes.slice(1).forEach(
                n => this._deleteSubspan(n.subspanId)
            );
            this._convertToTree();
        }
    }

    _splitDiscontiguous() {
        this.nodes = this.nodes.map(
            n => n.splitSpan()
        ).flat();
    }

    createSpanFromToken(
        tokenizationId: UUID, spanId: UUID,
        tokenIndex: number, type: string, mentionModelType: MentionModel
    ) {
        const newSpan: TaggedTokenSpan = {
            tokenizationId: tokenizationId,
            spanId: spanId,
            tokenIndexList: [tokenIndex],
            type: type,
            mentionModel: mentionModelType
        }

        const newNode = new SpanNode(newSpan);

        this.nodes.push(newNode);

        return this;
    }

    createSpanFromTokenList(
        tokenizationId: UUID, spanId: UUID,
        tokenList: number[], type: string, mentionModelType: MentionModel
    ) {
        const newSpan: TaggedTokenSpan = {
            tokenizationId: tokenizationId,
            spanId: spanId,
            tokenIndexList: tokenList,
            type: type,
            mentionModel: mentionModelType
        }

        const newNode = new SpanNode(newSpan);

        this.nodes.push(newNode);

        return this;
    }

    addTokenToSpan(
        tokenizationId: UUID, spanId: UUID,
        tokenIndex: number, type: string, mentionModelType: MentionModel
    ) {
        this.createSpanFromToken(
            tokenizationId, spanId,
            tokenIndex, type, mentionModelType
        )
        this._mergeSubspansOf(spanId);

        return this;
    }

    updateType(spanId: UUID, type: string) {
        for (const node of this._findSpanNodes(spanId)) {
            node.type = type;
        }

        return this;
    }

    updateId(spanId: UUID, id: string) {
        for (const node of this._findSpanNodes(spanId)) {
            node.span.ID = id;
        }

        return this;
    }

    mergeSpans(spanId1: UUID, spanId2: UUID) {
        const span2Nodes = this._findSpanNodes(spanId2);

        for (const node2 of span2Nodes) {
            node2.spanId = spanId1;
        }

        this._mergeSubspansOf(spanId1);

        return this;
    }

    dragSelectTokenRange(tokenIdx1:number, tokenIdx2:number) {
        
        const startIdx = Math.min(tokenIdx1,tokenIdx2);
        const endIdx = Math.max(tokenIdx1,tokenIdx2);

        const range = []

        for(var i= startIdx;i<=endIdx;i++){
            range.push(i)
        }

        return range;
    }

    /** 
     * The span nodes that properly contain this one.
     * 
     * Token nodes that are coextensive with a span node
     * are always considered to be properly contained by
     * the span node.
     * 
     * @param node
     */
    contains(node: SpanTreeNode) {
        this._sortNodes();

        return this.nodes.filter(
            n => (
                n.kind === "span" &&
                node.subspanId.uuidString !== n.subspanId.uuidString
                &&
                node.tokenIndexList.every(
                    i => n.tokenIndexList.includes(i)
                )
                &&
                (
                    node.kind === "token" ||
                    n.tokenIndexList.some(
                        i => !node.tokenIndexList.includes(i)
                    )
                )
            )
        );
    }

    /** 
     * The nodes that are properly contained by this one. 
     * 
     * Token nodes that are coextensive with a span node
     * are always considered to be properly contained by
     * the span node.
     * 
     * @param node
     */
    containedBy(node: SpanTreeNode) {
        this._sortNodes();

        if (node.kind === "token") {
            return [];
        } else {
            return this.nodes.filter(
                n => (
                    node.subspanId.uuidString !== n.subspanId.uuidString
                    &&
                    n.tokenIndexList.every(
                        i => node.tokenIndexList.includes(i)
                    )
                    &&
                    (
                        n.kind === "token" ||
                        node.tokenIndexList.some(
                            i => !n.tokenIndexList.includes(i)
                        )
                    )
                )
            );
        }
    }

    /** 
     * The nodes that are coextensive with this one. 
     * 
     * @param node
     */
    coextensiveWith(node: SpanTreeNode) {
        this._sortNodes();

        return this.nodes.filter(
            n => (
                node.subspanId.uuidString !== n.subspanId.uuidString
                &&
                n.tokenIndexList.every(
                    i => node.tokenIndexList.includes(i)
                )
                &&
                node.tokenIndexList.every(
                    i => n.tokenIndexList.includes(i)
                )
            )
        );
    }

    /**
     * The nodes that are contained by this one but not by any other.
     * 
     * In the case where there are nodes coextesive with this one, 
     * children are determined by the lexicographic ordering on 
     * the node's subspanId.
     * 
     * @param node 
     */
    childrenOf(node: SpanTreeNode): SpanTreeNode[] {
        // tokens never have children
        if (node.kind === "token") {
            return [];
        }

        const coextensiveWith = this.coextensiveWith(node);

        // get all the token nodes that are coextensive with this one
        const coextensiveSpans = coextensiveWith.filter(
            (n: SpanTreeNode): n is SpanNode => n.kind === "span"
        );

        // get all the span nodes that are coextensive with this one
        const coextensiveTokens = coextensiveWith.filter(
            (n: SpanTreeNode): n is TokenNode => n.kind === "token"
        );

        // filter the span nodes for ones with UUIDs ranked below
        // those lexicographically
        const coextensiveChildSpans = coextensiveSpans.filter(
            n => node.subspanId.uuidString > n.subspanId.uuidString
        );

        // if there are such nodes...
        if (coextensiveChildSpans.length) {
            // sort them by UUID rank
            coextensiveChildSpans.sort(
                (n1, n2) => (
                    n1.subspanId.uuidString > n2.subspanId.uuidString
                        ? -1
                        : 1
                )
            );

            // return the highest ranked
            return [coextensiveChildSpans[0]];
        } else if (coextensiveTokens.length) {
            return coextensiveTokens;
        }

        // get all the nodes properly contained by this one
        const containedBy = this.containedBy(node);

        // of those, find the ones...
        const children = containedBy.filter(
            n1 => (
                // ...that are not contained by...
                !this.contains(n1).some(
                    // ...some other node contained by this one
                    n2 => containedBy.some(
                        n3 => n2.subspanId.uuidString === n3.subspanId.uuidString 
                    )
                ) &&
                // ...and that are maximal relative to any coextensive span
                this.coextensiveWith(n1).filter(
                    n2 => n2.kind === "span"
                ).every(
                    n2 => n2.subspanId.uuidString < n1.subspanId.uuidString
                )
            )
        );

        return children;
    }

    /** 
     * The nodes that overlap with this one (excluding the node itself).
     * 
     * This method is private because it is only used on initialization
     * in order to ensure that the graph is in fact a tree.
     * 
     * @param node
     * @param includeContained
     * whether to include the nodes containing or contained by this one.
     */
    _overlaps(node: SpanTreeNode, includeContained: boolean = false) {
        const overlapping = this.nodes.filter(
            n => (
                n.subspanId.uuidString !== node.subspanId.uuidString
                &&
                n.tokenIndexList.some(
                    i => node.tokenIndexList.includes(i)
                )

            )
        );

        if (includeContained) {
            return overlapping;
        } else {
            return overlapping.filter(
                n => !(
                    n.tokenIndexList.every(
                        i => node.tokenIndexList.includes(i)
                    )
                    ||
                    node.tokenIndexList.every(
                        i => n.tokenIndexList.includes(i)
                    )
                )
            );
        }

    }

    /** The nodes not contained by any other node. */
    get maxima() {
        this._sortNodes();

        return this.nodes.filter(
            n1 => this.nodes.every(
                n2 => this.childrenOf(n2).every(
                    n3 => n1.subspanId.uuidString !== n3.subspanId.uuidString
                )
            )
        );
    }

    _compareNodes(
        node1: SpanTreeNode, node2: SpanTreeNode, reverse: boolean = false
    ) {
        const node1Start = Math.min(...node1.tokenIndexList)
        const node1End = Math.max(...node1.tokenIndexList)

        const node2Start = Math.min(...node2.tokenIndexList)
        const node2End = Math.max(...node2.tokenIndexList)

        const node1Size = (node1End - node1Start);
        const node2Size = (node2End - node2Start);

        const startDiff = reverse
            ? node2Start - node1Start
            : node1Start - node2Start;

        const sizeDiff = node2Size - node1Size;

        // if they don't start at the same place.
        if (startDiff !== 0) {
            // sort by the start position
            return startDiff;
        } else if (sizeDiff !== 0) {
            // sort by size (descending)
            return sizeDiff;
        } else {
            return (
                node1.subspanId.uuidString < node2.subspanId.uuidString
                    ? 1
                    : -1
            );
        }
    }

    _sortNodes(reverse: boolean = false) {
        this.nodes.sort(
            (n1, n2) => this._compareNodes(n1, n2, reverse)
        );
    }

    _convertToTree() {
        this._splitDiscontiguous();

        // sort the nodes in descending order based on start
        this._sortNodes(true);

        // find nodes that overlap with some other node
        let overlapping = this.nodes.map(
            n => ({ "node": n, "overlappingSpans": this._overlaps(n) })
        ).filter(
            l => l.overlappingSpans.length > 0
        );

        let hasOverlaps = overlapping.length > 0;

        // if there are overlaps...
        while (hasOverlaps) {
            // ...find the intersection between the first span in the list
            // and the first span in the list of spans it overlaps
            // these spans are sorted in reverse order, so the span we will
            // split should start later
            const intersection = overlapping[0].node.tokenIndexList.filter(
                i => overlapping[0].overlappingSpans[0].tokenIndexList.includes(i)
            );
            const splitIndex = Math.max(...intersection);
            const newNodes = overlapping[0].node.splitSpan(splitIndex);

            // remove the node we just split
            this.nodes = this.nodes.filter(
                n => n.subspanId !== overlapping[0].node.subspanId
            );

            // add the new nodes
            this.nodes = this.nodes.concat(newNodes);

            // find nodes that still overlap with some other node
            overlapping = this.nodes.map(
                n => ({ "node": n, "overlappingSpans": this._overlaps(n) })
            ).filter(
                l => l.overlappingSpans.length > 0
            );

            hasOverlaps = overlapping.length > 0;
        }

        // sort the nodes in ascending order based on start
        this._sortNodes();
    }
}